import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCredits } from '../context/CreditContext';
import { IMG_MODELS, RATIOS } from '../constants/models';
import { useCostCalculator } from '../hooks/useCostCalculator';
import * as api from '../services/api';
import Icons from '../components/Icons';
import Lightbox from '../components/Lightbox';
import MediaLibraryModal from '../components/MediaLibraryModal';
import { groupItemsByDate } from '../utils/grouping';
import toast from 'react-hot-toast';
import { compressImage } from '../utils/imageCompression';
import { downloadFile } from '../utils/downloadFile';
import { confirmAlert } from '../utils/confirmAlert';
import AspectRatioSelector from '../components/AspectRatioSelector';
import TemplateSlideWindow from '../components/TemplateSlideWindow';
import { handleSafetyError } from '../utils/handleSafetyError';

// Default resolution options
const DEFAULT_RES = [
  { id: '1k', label: '1K', desc: '1024px', mult: 1 },
  { id: '2k', label: '2K', desc: '2048px', mult: 1.5 },
  { id: '3k', label: '3K', desc: '3072px', mult: 2.0 },
  { id: '4k', label: '4K', desc: '4096px', mult: 2.5 },
];

const QUALITY_OPTS = [
  { id: 'low',    label: 'Low',    desc: 'Fastest' },
  { id: 'medium', label: 'Medium', desc: 'Balanced' },
  { id: 'high',   label: 'High',   desc: 'Best quality' },
];

export default function GeneratePage({ onShowAuth }) {
  const { user, token } = useAuth();
  const { credits, setCredits, items, refreshItems, removeItem, pendingJobs, setPendingJobs, plan } = useCredits();
  const [showContentFilterModal, setShowContentFilterModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const [prompt, setPrompt] = useState('');
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const [negPrompt, setNegPrompt] = useState('');
  const [model, setModel] = useState('nano-banana-2');
  const [ratio, setRatio] = useState('1:1');
  const [showNeg, setShowNeg] = useState(false);
  const [loading, setLoading] = useState(false);
  const [genImgs, setGenImgs] = useState([]);
  const [fullscreen, setFullscreen] = useState(false);
  const [fsIdx, setFsIdx] = useState(0);
  const [cd, setCd] = useState(0);
  const [lbItem, setLbItem] = useState(null);
  const [refFiles, setRefFiles] = useState([]);
  const [libOpen, setLibOpen] = useState(false);
  const [showModelPicker, setShowModelPicker] = useState(false);
  const [numImages, setNumImages] = useState(1);
  const [imgRes, setImgRes] = useState('1k');
  const [quality, setQuality] = useState('medium');

  const [showSuggest, setShowSuggest] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const fileRef = useRef();
  const cdRef = useRef();
  const textRef = useRef(null);
  const genLockRef = useRef(false);

  useEffect(() => {
    if (textRef.current) {
      textRef.current.style.height = 'auto';
      textRef.current.style.height = textRef.current.scrollHeight + 'px';
    }
  }, [prompt]);

  // ── Model-aware computed values ──
  const currentModel = IMG_MODELS.find(m => m.id === model);

  // Resolution options per model
  const resOptions = useMemo(() => {
    if (!currentModel) return DEFAULT_RES;
    if (currentModel.hideResolution) return [];
    const allowed = currentModel.resolutions || ['1k', '2k', '4k'];
    const customMult = currentModel.resMult;
    return DEFAULT_RES.filter(r => allowed.includes(r.id)).map(r => ({
      ...r,
      mult: customMult ? customMult[r.id] : r.mult,
    }));
  }, [currentModel]);

  // Aspect ratio per model
  const ratioOptions = useMemo(() => {
    return currentModel?.ratios || RATIOS;
  }, [currentModel]);

  // Max reference files
  const maxRefs = currentModel?.maxRefs ?? 4;
  const showRefs = maxRefs > 0;

  // Show quality selector
  const showQuality = currentModel?.hasQuality || false;

  // Show resolution
  const showResolution = resOptions.length > 0;

  const isModelLocked = useCallback((id) => {
    const currentPlan = plan || 'free';
    
    if (['flux-2-max', 'flux-2-flex'].includes(id)) {
      return ['basic', 'go'].includes(currentPlan);
    }
    if (['nano-banana-pro', 'flux-2-pro'].includes(id)) {
      return currentPlan === 'basic';
    }
    
    return false;
  }, [plan]);

  useEffect(() => {
    if (isModelLocked(model)) {
      const fallback = IMG_MODELS.find(m => !isModelLocked(m.id))?.id || IMG_MODELS[0].id;
      setModel(fallback);
    }
  }, [plan, model, isModelLocked]);

  // Set model if passed from router
  useEffect(() => {
    const st = location.state;
    if (st?.modelId) {
      const cleanModel = String(st.modelId).trim().toLowerCase();
      let newModel = st.modelId;
      
      const m = IMG_MODELS.find(x => 
        x.id.toLowerCase() === cleanModel || 
        x.label.toLowerCase() === cleanModel ||
        x.id.toLowerCase() === cleanModel.replace(/\s+/g, '-')
      );
      if (m) newModel = m.id;

      setModel(newModel);
      
      if (m?.ratios && !m.ratios.includes(ratio)) {
        setRatio(m.ratios[0] || '1:1');
      }
      if (m?.hideResolution) {
        setImgRes('1k');
      } else {
        const allowed = m?.resolutions || ['1k', '2k', '4k'];
        if (!allowed.includes(imgRes)) setImgRes(allowed[0]);
      }
      
      const newState = { ...st };
      delete newState.modelId;
      navigate(location.pathname, { replace: true, state: newState });
    }
  }, [location.state, navigate, ratio, imgRes]);

  // Handle incoming state from references
  const incomingProcessed = useRef(false);

  const processReferenceImage = useCallback(async (sourceUrl, actionPrompt, actionName) => {
    try {
      if (!sourceUrl || typeof sourceUrl !== 'string') return;
      let fetchUrl = sourceUrl;
      if (sourceUrl.startsWith('http')) {
        const API_BASE = process.env.REACT_APP_API_URL || '/api';
        fetchUrl = `${API_BASE}/proxy-image?url=${encodeURIComponent(sourceUrl)}`;
      }

      const resp = await fetch(fetchUrl);
      if (!resp.ok) throw new Error('Failed to fetch image');
      const blob = await resp.blob();
      const file = new File([blob], 'reference.jpg', { type: blob.type || 'image/jpeg' });
      const compressed = await compressImage(file);

      if (actionName === 'recreate') {
        // Set the uploaded image as a visual reference (prompt copying is disabled as per request)
        setRefFiles(prev => {
          const max = currentModel?.maxRefs || 4;
          if (prev.length < max) return [...prev, compressed];
          return prev;
        });
        toast.success('Image loaded as visual reference');
      } else if (actionName === 'use-as-reference') {
         setRefFiles(prev => {
            const max = currentModel?.maxRefs || 4;
            if (prev.length < max) return [...prev, compressed];
            return prev;
         });
         toast.success("Image set as reference");
      }
    } catch (e) {
      console.error("Failed to load incoming reference:", e);
      toast.error("Failed to load reference image");
      throw e;
    }
  }, [currentModel]);

  useEffect(() => {
    const st = location.state;
    
    // Auto-populating optimized prompt from /prompts
    if (st?.optimizedPrompt) {
      setPrompt(st.optimizedPrompt);
      if (textRef.current) {
        textRef.current.style.height = 'auto';
        textRef.current.style.height = Math.min(textRef.current.scrollHeight, 250) + 'px';
      }
      navigate(location.pathname, { replace: true, state: { ...st, optimizedPrompt: undefined } });
    }

    // Apply template text settings even if there is no image
    if (st?.prompt) {
      setPrompt(st.prompt);
    }

    if (!st || !st.action) return;

    if (!st.sourceUrl) {
      navigate(location.pathname, { replace: true, state: { ...st, action: undefined, prompt: undefined } });
      return;
    }
    
    // Check if we already processed this exact URL and action combination
    if (incomingProcessed.current === st.sourceUrl + st.action) return;
    incomingProcessed.current = st.sourceUrl + st.action;

    const setupIncoming = async () => {
      toast.loading('Loading template media...', { id: 'tmpl-load' });
      try {
        if (st.refImageUrls && st.refImageUrls.length > 0) {
          for (const url of st.refImageUrls) {
            await processReferenceImage(url, st.prompt, st.action);
          }
        } else if (st.sourceUrl) {
          await processReferenceImage(st.sourceUrl, st.prompt, st.action);
        }
        toast.success('Template loaded!', { id: 'tmpl-load' });
      } catch (e) {
        toast.error('Failed to load template media', { id: 'tmpl-load' });
      }
      // clear state safely
      navigate(location.pathname, { replace: true, state: { ...st, sourceUrl: undefined, refImageUrls: undefined, action: undefined, prompt: undefined } });
    };

    setupIncoming();
  }, [location.state, location.pathname, navigate, processReferenceImage]);

  // Adjust textarea height on prompt change programmatically
  useEffect(() => {
    if (textRef.current) {
      textRef.current.style.height = 'auto';
      textRef.current.style.height = textRef.current.scrollHeight + 'px';
    }
  }, [prompt]);

  // Reset ratio when model changes and model has custom ratios
  const handleModelChange = (newModel) => {
    if (isModelLocked(newModel)) {
      setShowModelPicker(false);
      navigate('/subscription');
      return;
    }
    setModel(newModel);
    setShowModelPicker(false);
    const m = IMG_MODELS.find(x => x.id === newModel);
    // Reset ratio if new model has custom ratios and current ratio is not in the list
    if (m?.ratios && !m.ratios.includes(ratio)) {
      setRatio(m.ratios[0] || '1:1');
    }
    // Reset resolution if new model hides it or current res is not available
    if (m?.hideResolution) {
      setImgRes('1k');
    } else {
      const allowed = m?.resolutions || ['1k', '2k', '4k'];
      if (!allowed.includes(imgRes)) setImgRes(allowed[0]);
    }
    // Trim references to new model's limit
    const newMax = m?.maxRefs ?? 4;
    if (refFiles.length > newMax) {
      setRefFiles(prev => prev.slice(0, newMax));
    }
  };

  const handlePromptInput = (e) => {
    const val = e.target.value;
    setPrompt(val);
    
    const target = e.target;
    target.style.height = '0px';
    target.style.height = target.scrollHeight + 'px';
    
    if (refFiles.length > 0) {
      const cursorIndex = e.target.selectionStart;
      const textBeforeCursor = val.slice(0, cursorIndex);
      if (textBeforeCursor.match(/@[\w]*$/)) {
        setShowSuggest(true);
      } else {
        setShowSuggest(false);
      }
    } else {
      setShowSuggest(false);
    }
  };

  const insertSuggestion = (imgIdx) => {
    const imgName = `@image${imgIdx + 1} `;
    if (textRef.current) {
      const cursorIndex = textRef.current.selectionStart;
      const textBeforeCursor = prompt.slice(0, cursorIndex);
      const textAfterCursor = prompt.slice(cursorIndex);
      
      const newTextBefore = textBeforeCursor.replace(/@[\w]*$/, imgName);
      const newPrompt = newTextBefore + textAfterCursor;
      setPrompt(newPrompt);
      setShowSuggest(false);
      
      setTimeout(() => {
        if (textRef.current) {
          textRef.current.focus();
          textRef.current.setSelectionRange(newTextBefore.length, newTextBefore.length);
        }
      }, 0);
    } else {
      setPrompt(prompt.replace(/@[\w]*$/, imgName));
      setShowSuggest(false);
    }
  };

  const handleOptimize = async () => {
    if (!prompt.trim()) {
      toast.error('Enter a basic prompt to optimize first.');
      return;
    }
    navigate('/prompt', { state: { prompt, mode: 'image', modelId: model } });
  };

  // Cost calculation — uses hook to handle GPT Image 2 quality pricing
  const cost = useCostCalculator({
    mode: 'image',
    model,
    quality,
    aspectRatio: ratio,
    numImages,
    resolution: imgRes,
  });

  const startCD = (s) => {
    setCd(s);
    if (cdRef.current) clearInterval(cdRef.current);
    cdRef.current = setInterval(() => {
      setCd(p => { if (p <= 1) { clearInterval(cdRef.current); return 0; } return p - 1; });
    }, 1000);
  };

  const handleGen = useCallback(async () => {
    if (genLockRef.current) return;
    if (cd > 0) { toast.error(`Wait ${cd}s`); return; }
    if (!prompt.trim()) { toast.error('Enter a prompt'); return; }
    if (!user) { if (onShowAuth) onShowAuth(); return; }
    if (credits < cost) { toast.error(`Need ${cost} credits`); navigate('/subscription'); return; }
    genLockRef.current = true;

    // Create pending job placeholders (non-blocking)
    const jobIds = Array.from({ length: numImages }, (_, i) => `pend_${Date.now()}_${i}`);
    const newJobs = jobIds.map(id => ({
      id, prompt: prompt.substring(0, 40), model: currentModel?.label || model, type: 'image',
    }));
    setPendingJobs(prev => [...newJobs, ...prev]);
    setLoading(true);
    startCD(8);

    // Fire generation in background
    (async () => {
      const allResults = [];
      try {
      for (let i = 0; i < numImages; i++) {
        try {
          // Step 1: Submit — returns jobId instantly
          const res = await api.generateImage(token, {
            prompt, model, aspectRatio: ratio, negativePrompt: negPrompt,
            references: refFiles.map(r => r.fileUrl || r), imageResolution: imgRes,
            quality: showQuality ? quality : undefined,
            nsfwChecker: false,
          });

          if (res.jobId) {
            // Step 2: New async queue — poll until done
            if (res.creditsRemaining !== undefined) setCredits(res.creditsRemaining);
            const job = await api.pollJob(token, res.jobId, () => {}, 300000);
            const url = job.resultData?.imageUrl || job.resultUrl;
            if (url) allResults.push(url);
            if (job.creditsRemaining !== null && job.creditsRemaining !== undefined) setCredits(job.creditsRemaining);
          } else if (res.success) {
            // Legacy sync response
            allResults.push(res.imageUrl);
            setCredits(res.creditsRemaining);
          }
        } catch (e) {
          if (handleSafetyError(e)) break; // SafetyBanner handles display
          const msg = e.response?.data?.error || e.message || '';
          const st = e.response?.status;
          const isFiltered = msg.includes('CONTENT_FILTERED') || msg.toLowerCase().includes('moderated') || msg.toLowerCase().includes('safety filter');
          if (isFiltered) {
            setShowContentFilterModal(true);
            break;
          } else if (st === 429) { toast.error(msg || 'Rate limited'); startCD(20); break; }
          else if (st === 402) { toast.error('Not enough credits'); break; }
          else if (i === 0) toast.error(msg || 'Generation failed. Credits refunded.');
        }
      }
      // Remove pending jobs
      setPendingJobs(prev => prev.filter(j => !jobIds.includes(j.id)));
      if (allResults.length > 0) {
        setGenImgs(allResults);
        toast.success(`${allResults.length} image${allResults.length > 1 ? 's' : ''} generated!`);
        refreshItems(true);
      }
      } finally {
      setPendingJobs(prev => prev.filter(j => !jobIds.includes(j.id)));
      genLockRef.current = false;
      setLoading(false);
      }
    })();
  }, [prompt, model, ratio, negPrompt, refFiles, cost, credits, cd, token, user, numImages, imgRes, quality, showQuality, navigate, setCredits, refreshItems, onShowAuth, currentModel]);

  const handleDeleteItem = async (id) => {
    const isConfirmed = await confirmAlert({
      title: 'Delete Creation?',
      message: 'Are you sure you want to delete this creation? This action cannot be undone.'
    });
    if (!isConfirmed) return;
    try {
      await api.deleteGeneration(token, id);
      removeItem(id);
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  // Action handlers for generated image
  const goRecreate = () => {
    if (genImgs[0]) {
      processReferenceImage(genImgs[0], prompt, 'recreate');
    }
  };
  const goUpscale = () => {
    navigate('/edit', { state: { sourceUrl: genImgs[0], prompt, mode: 'upscale', fromLightbox: true } });
  };
  const goVariation = () => {
    navigate('/edit', { state: { sourceUrl: genImgs[0], prompt, mode: 'variation', fromLightbox: true } });
  };
  const goImageToVideo = () => {
    navigate('/video', { state: { sourceUrl: genImgs[0] } });
  };
  const goEdit = () => {
    navigate('/edit', { state: { sourceUrl: genImgs[0], prompt, mode: 'edit', fromLightbox: true } });
  };
  const toggleFullscreen = (idx = 0) => { setFsIdx(idx); setFullscreen(!fullscreen); };
  const genImg = genImgs[0] || null;

  const recentImages = items
    .filter(i => i.type !== 'video')
    .sort((a, b) => {
      const ta = a.createdAt?.seconds || a.createdAt?._seconds || 0;
      const tb = b.createdAt?.seconds || b.createdAt?._seconds || 0;
      return tb - ta;
    })
    .slice(0, 12);

  const localJobs = pendingJobs.filter(j => j.type === 'image');

  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const longPressTimer = useRef(null);
  const longPressFired = useRef(false);

  const startLongPress = (id) => {
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      setIsSelectionMode(true);
      setSelectedIds(prev => prev.includes(id) ? prev : [...prev, id]);
    }, 500);
  };

  const cancelLongPress = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
  };

  const handleBatchDelete = async () => {
    if (selectedIds.length === 0) return;
    const isConfirmed = await confirmAlert({
      title: 'Delete Selected Items?',
      message: `Are you sure you want to delete ${selectedIds.length} items? This action cannot be undone.`
    });
    if (!isConfirmed) return;
    setIsDeleting(true);
    try {
      await api.batchDeleteGenerations(token, selectedIds);
      selectedIds.forEach(id => removeItem(id));
      setSelectedIds([]);
      setIsSelectionMode(false);
      toast.success(`Successfully deleted ${selectedIds.length} items`);
    } catch {
      toast.error('Batch delete failed');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBatchDownload = async () => {
    if (selectedIds.length === 0) return;
    setIsDownloading(true);
    try {
      const itemsToDownload = recentImages.filter(i => selectedIds.includes(i.id));
      for (let i = 0; i < itemsToDownload.length; i++) {
        const itemContext = itemsToDownload[i];
        const url = itemContext.videoUrl || itemContext.imageUrl;
        const isImage = !itemContext.videoUrl;
        
        try {
           const API_URL = process.env.REACT_APP_API_URL || '/api';
           const res = await fetch(`${API_URL}/download?url=${encodeURIComponent(url)}`, {
             headers: { Authorization: `Bearer ${token}` },
           });
           if (res.ok) {
             const blob = await res.blob();
             const ext = isImage ? 'png' : 'mp4';
             const a = document.createElement('a');
             a.href = URL.createObjectURL(blob);
             a.download = `aifield_${Date.now()}_${i}.${ext}`;
             document.body.appendChild(a);
             a.click();
             document.body.removeChild(a);
             window.URL.revokeObjectURL(a.href);
           }
        } catch (e) {
           console.error('Download err:', e);
        }
        await new Promise(r => setTimeout(r, 400));
      }
      toast.success(`Started download for ${itemsToDownload.length} items`);
      setIsSelectionMode(false);
      setSelectedIds([]);
    } catch {
      toast.error('Batch download failed');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      {/* ── Content Filter Warning Modal ── */}
      {showContentFilterModal && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px',
          }}
          onClick={() => setShowContentFilterModal(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'linear-gradient(135deg, #1a0a0a 0%, #120c0c 100%)',
              border: '1px solid rgba(239,68,68,0.35)',
              borderRadius: '20px',
              padding: '36px 32px',
              maxWidth: '440px',
              width: '100%',
              boxShadow: '0 0 60px rgba(239,68,68,0.2), 0 20px 60px rgba(0,0,0,0.6)',
              textAlign: 'center',
            }}
          >
            {/* Icon */}
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: 'rgba(239,68,68,0.15)',
              border: '2px solid rgba(239,68,68,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: 28,
            }}>🚫</div>

            {/* Title */}
            <div style={{ fontSize: 20, fontWeight: 800, color: '#f87171', marginBottom: 10 }}>
              Content Policy Violation
            </div>

            {/* Message */}
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.65)', lineHeight: 1.7, marginBottom: 8 }}>
              Your prompt was detected as <strong style={{ color: '#fca5a5' }}>sensitive or adult content</strong> by our AI safety filters.
            </div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', lineHeight: 1.6, marginBottom: 24 }}>
              Because the AI provider was still called and charged for this request, <strong style={{ color: '#fca5a5' }}>credits have been deducted</strong>. Please revise your prompt and avoid sexual, violent, or otherwise prohibited content.
            </div>

            {/* Warning box */}
            <div style={{
              background: 'rgba(239,68,68,0.08)',
              border: '1px solid rgba(239,68,68,0.2)',
              borderRadius: 10, padding: '12px 16px',
              fontSize: 12, color: 'rgba(255,255,255,0.5)',
              marginBottom: 24, lineHeight: 1.6,
            }}>
              ⚠️ Repeated violations may result in your account being reviewed or suspended.
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setShowContentFilterModal(false)}
                style={{
                  flex: 1, padding: '12px 0', borderRadius: 10,
                  background: 'rgba(239,68,68,0.15)',
                  border: '1px solid rgba(239,68,68,0.3)',
                  color: '#f87171', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                  transition: 'all .2s',
                }}
              >
                I Understand
              </button>
              <button
                onClick={() => { setShowContentFilterModal(false); }}
                style={{
                  flex: 1, padding: '12px 0', borderRadius: 10,
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: 'rgba(255,255,255,0.6)', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                  transition: 'all .2s',
                }}
              >
                Edit Prompt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen overlay */}
      {fullscreen && genImgs[fsIdx] && (
        <div className="img-fs" onClick={() => setFullscreen(false)}>
          <img src={genImgs[fsIdx]} alt="" className="img-fs-img" onClick={e => e.stopPropagation()} />
          <button className="img-fs-close" onClick={() => setFullscreen(false)}>
            <Icons.X sz={16} />
          </button>
        </div>
      )}

      {/* ── Model Picker Popup ── */}
      {showModelPicker && (
        <div className="mp-overlay" onClick={() => setShowModelPicker(false)}>
          <div className="mp-popup" onClick={e => e.stopPropagation()}>
            <div className="mp-header">
              <div className="mp-title">Select Model</div>
              <button className="mc-btn" onClick={() => setShowModelPicker(false)}>
                <Icons.X sz={14} />
              </button>
            </div>
            <div className="mp-body">
              {IMG_MODELS.map(m => {
                const locked = isModelLocked(m.id);
                return (
                <button
                  key={m.id}
                  className={`mp-item ${model === m.id ? 'on' : ''}`}
                  onClick={() => handleModelChange(m.id)}
                >
                  {m.icon && Icons[m.icon] ? (
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', width: 20, height: 20, marginRight: 10, color: m.color }}>
                      {React.createElement(Icons[m.icon], { sz: 18, color: m.color })}
                    </div>
                  ) : <div className="mp-dot" style={{ background: m.color }} />}
                  <div className="mp-info">
                    <div className="mp-name">
                      {m.label}
                    </div>
                    {m.desc && <div className="mp-desc" style={{ fontSize: 11, color: 'var(--t3)', marginTop: 2 }}>{m.desc}</div>}
                  </div>
                  <div className="mp-badges">
                    {locked && <span className="mp-badge" style={{background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)'}}>Premium</span>}
                    {!locked && m.popular && <span className="mp-badge pop">Popular</span>}
                    {!locked && m.tag && <span className="mp-badge tag">{m.tag}</span>}
                  </div>
                  {locked ? (
                    <div className="mp-lock">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                    </div>
                  ) : model === m.id && (
                    <div className="mp-check">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                  )}
                </button>
              )})}
            </div>
          </div>
        </div>
      )}

      {/* Mobile FAB */}
      <button className={`mobile-fab ${showMobilePreview ? 'on' : ''}`} onClick={() => setShowMobilePreview(!showMobilePreview)}>
        <Icons.ImgFrame sz={22} />
        <span>{showMobilePreview ? 'Close' : 'Preview'}</span>
      </button>

      {/* ── Settings Panel ── */}
      <section className="panel prompt-panel">
        <div className="phead"><Icons.Sparkle sz={16} /> Generate Image</div>

        {/* Model Selection — Click to open popup */}
        <div className="psec">
          <div className="slbl">Model</div>
          <button className="mp-trigger" onClick={() => setShowModelPicker(true)}>
            <div className="mp-trigger-left">
              {currentModel?.icon && Icons[currentModel.icon] ? (
                <div style={{ display:'flex', alignItems:'center', justifyContent:'center', width: 22, height: 22, marginRight: 10, color: currentModel.color }}>
                  {React.createElement(Icons[currentModel.icon], { sz: 20, color: currentModel.color })}
                </div>
              ) : <div className="mp-dot" style={{ background: currentModel?.color }} />}
              <div>
                <div className="mp-trigger-name">{currentModel?.label}</div>
              </div>
            </div>
            <div className="mp-trigger-right">
              {currentModel?.popular && <span className="mp-badge pop">Popular</span>}
              {currentModel?.tag && <span className="mp-badge tag">{currentModel?.tag}</span>}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
            </div>
          </button>
        </div>

        {/* Visual References — ABOVE Prompt, only if model supports it */}
        {showRefs && (
          <div className="psec">
            <div className="slbl">Visual References <span className="opt">— max {maxRefs}</span></div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {refFiles.map((f, i) => (
                <div key={i} style={{
                  position: 'relative', width: 44, height: 44, borderRadius: 8,
                  overflow: 'hidden', border: '1px solid rgba(139,92,246,.4)',
                }}>
                  <img src={f.fileUrl || (f instanceof Blob || f instanceof File ? URL.createObjectURL(f) : '')} alt="" style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                  <div style={{
                    position: 'absolute', bottom: 0, width: '100%', background: 'rgba(0,0,0,.7)',
                    textAlign: 'center', fontSize: 9, fontWeight: 700, color: '#a78bfa'
                  }}>@{i + 1}</div>
                  <button onClick={() => setRefFiles(prev => prev.filter((_, j) => j !== i))} style={{
                    position: 'absolute', top: 1, right: 1, width: 14, height: 14, borderRadius: '50%',
                    background: 'rgba(0,0,0,.7)', border: 'none', color: '#fff', fontSize: 9, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1
                  }}>✕</button>
                </div>
              ))}
              {refFiles.length < maxRefs && (
                <button onClick={() => setLibOpen(true)} style={{
                  width: 44, height: 44, borderRadius: 8, border: '1px dashed rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.03)', color: 'var(--t2)', cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2
                }}>
                  <Icons.Plus sz={12} />
                </button>
              )}
            </div>

            <div style={{ fontSize: 10, color: 'var(--t3)', marginTop: 8, fontStyle: 'italic' }}>
              Type <span style={{color: '#a78bfa', fontWeight: 'bold'}}>@</span> in your prompt to refer to uploaded media.
            </div>
          </div>
        )}

        {/* Prompt */}
        <div className="psec">
          <div className="slbl">Prompt</div>
          <div className="pwrap" style={{ position: 'relative' }}>
            <textarea ref={textRef} className="ptxt"
              placeholder="Hint: [Subject] + [Action/Context] + [Environment/Background] + [Lighting/Mood] + [Art Style/Camera Settings] + [Technical Quality Tags]"
              value={prompt} onChange={handlePromptInput} maxLength={10000} 
              style={{ minHeight: '120px', fontSize: '13px', resize: 'none', overflowY: 'hidden', flex: 'none', display: 'block' }} />
            {showSuggest && (
              <div style={{
                position: 'absolute', bottom: 'calc(100% + 4px)', left: 0, background: '#1e1e2d',
                border: '1px solid rgba(139,92,246,0.5)', borderRadius: 8, padding: 6, zIndex: 100,
                boxShadow: '0 8px 24px rgba(0,0,0,0.6)', display: 'flex', gap: 6,
              }}>
                {refFiles.map((img, idx) => (
                  <div key={idx} onMouseDown={(e) => { e.preventDefault(); insertSuggestion(idx); }} style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px',
                    cursor: 'pointer', borderRadius: 6, background: 'rgba(139,92,246,0.15)',
                    border: '1px solid rgba(139,92,246,0.2)', transition: 'background 0.2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(139,92,246,0.3)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(139,92,246,0.15)'}>
                    <img src={img.fileUrl || (img instanceof Blob || img instanceof File ? URL.createObjectURL(img) : '')} alt="" style={{width: 24, height: 24, borderRadius: 4, objectFit: 'cover'}} />
                    <span style={{fontSize: 12, color: '#a78bfa', fontWeight: 600}}>@image{idx+1}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="pbot">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span className="pcnt" style={{ fontSize: '10px' }}>{prompt.length}/10000</span>
                {prompt.length > 0 && (
                  <button 
                    onClick={() => setShowClearConfirm(true)} 
                    style={{ background: 'transparent', border: 'none', color: 'var(--rd)', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px' }}
                    title="Clear prompt"
                  >
                    <Icons.X sz={14} />
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button 
                  className="tbtn" 
                  onClick={() => setShowTemplates(true)} 
                  style={{
                    background: 'rgba(245,158,11,0.1)',
                    border: '1px solid rgba(245,158,11,0.2)', color: '#f59e0b', fontSize: 10,
                    padding: '4px 8px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 4,
                    cursor: 'pointer', zIndex: 5
                  }}
                >
                  <Icons.Sparkle sz={10} /> Templates
                </button>
                <button 
                  className="tbtn" 
                  onClick={handleOptimize} 
                  disabled={!prompt.trim()}
                  style={{
                    background: 'rgba(139,92,246,0.1)',
                    border: '1px solid rgba(139,92,246,0.2)', color: '#fff', fontSize: 10,
                    padding: '4px 8px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 4,
                    opacity: !prompt.trim() ? 0.5 : 1, cursor: 'pointer', zIndex: 5
                  }}
                >
                  <>
                    <Icons.Sparkle sz={10} /> Optimize
                  </>
                </button>
              </div>
            </div>
          </div>
          <div style={{ marginTop: 6 }}>
            <span style={{ fontSize: 11, color: 'var(--t3)', cursor: 'pointer' }} onClick={() => setShowNeg(!showNeg)}>
              {showNeg ? '▾' : '▸'} Negative prompt
            </span>
            {showNeg && (
              <textarea className="ptxt" style={{ minHeight: 50, marginTop: 4, padding: '8px 12px' }}
                placeholder="blurry, low quality, deformed…"
                value={negPrompt} onChange={e => setNegPrompt(e.target.value)} />
            )}
          </div>
        </div>

        <div className="psec">
          <div className="slbl">Aspect Ratio</div>
          <AspectRatioSelector
            options={ratioOptions}
            value={ratio}
            onChange={setRatio}
          />
        </div>

        {/* Resolution — conditional per model */}
        {showResolution && (
          <div className="psec">
            <div className="slbl">Resolution</div>
            <div className="pills">
              {resOptions.map(r => (
                <button key={r.id} className={`pill ${imgRes === r.id ? 'on' : ''}`}
                  onClick={() => setImgRes(r.id)}>
                  {r.label} <span style={{ fontSize: 9, opacity: .6 }}>({r.desc})</span>
                  {!currentModel?.resPrices && r.mult > 1 && <span style={{ fontSize: 9, color: 'var(--or)' }}> ×{r.mult}</span>}
                  {currentModel?.resPrices && r.id !== '1k' && <span style={{ fontSize: 9, color: 'var(--or)' }}> {currentModel.resPrices[r.id]}cr</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Quality — only for GPT Image 1.5 */}
        {showQuality && (
          <div className="psec">
            <div className="slbl">Quality</div>
            <div className="pills">
              {QUALITY_OPTS.map(q => (
                <button key={q.id} className={`pill ${quality === q.id ? 'on' : ''}`}
                  onClick={() => setQuality(q.id)}>
                  {q.label} <span style={{ fontSize: 8, opacity: .6 }}>({q.desc})</span>
                </button>
              ))}
            </div>
          </div>
        )}


        {/* Number of Images */}
        <div className="psec">
          <div className="slbl">Number of Images</div>
          <div className="num-selector">
            <button className="num-btn" onClick={() => setNumImages(Math.max(1, numImages - 1))} disabled={numImages <= 1}>−</button>
            <span className="num-val">{numImages}</span>
            <button className="num-btn" onClick={() => setNumImages(Math.min(4, numImages + 1))} disabled={numImages >= 4}>+</button>
            <span style={{ fontSize: 10, color: 'var(--t3)', marginLeft: 8 }}>max 4</span>
          </div>
        </div>

        {/* Sticky Generate Button */}
        <div className="gen-sticky">
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--t2)', marginBottom: 12, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, flexWrap: 'wrap', textAlign: 'center' }}>
            <strong style={{ color: currentModel?.color }}>{currentModel?.label}</strong>
            {numImages > 1 && <span>· <strong>{numImages} imgs</strong></span>}
            <span>· {ratio}</span>
            {showResolution && <span>· {imgRes.toUpperCase()}</span>}
          </div>
          <div className="cost-row">
            <span className="cost-l">Cost</span>
            <span className="cost-v">
              {cost} cr
              {numImages > 1 && ` (${singleCost}×${numImages})`}
              {' → '}{Math.max(0, credits - cost)} left
            </span>
          </div>
          <button className="gbtn" onClick={handleGen}
            disabled={loading || credits < cost || cd > 0}>
            {cd > 0 ? `Wait ${cd}s` :
              <><Icons.Sparkle sz={15} /> Generate{numImages > 1 ? ` (×${numImages})` : ''}</>}
          </button>
          {credits < cost && (
            <p style={{ textAlign: 'center', color: 'var(--rd)', fontSize: 11, marginTop: 6, cursor: 'pointer' }}
              onClick={() => navigate('/subscription')}>
              Insufficient credits — <u>upgrade</u>
            </p>
          )}
          {cd > 0 && <p style={{ textAlign: 'center', fontSize: 10, color: 'var(--or)', marginTop: 6 }}>Cooldown: {cd}s</p>}
        </div>
      </section>

      {/* ── Main Canvas ── */}
      <div className={`main ${showMobilePreview ? 'mobile-preview-open' : ''}`}>
        <div className="mtbar">
          <button className="mtab main-back-mobile" onClick={() => setShowMobilePreview(false)}>
            <Icons.X sz={13} /> Close
          </button>
          <div className="mtab on">Preview</div>
          <div style={{ flex: 1 }} />
          <div className="mtab" onClick={() => navigate('/gallery')} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Icons.Grid sz={13} /> Creations
          </div>
        </div>
        <div className="mbody" onClick={() => {
          if (isSelectionMode) {
            setIsSelectionMode(false);
            setSelectedIds([]);
          }
        }}>
          {genImgs.length > 0 && (
            <div className="prev">
              <div className="prev-wrap">
                  {/* Top toolbar — icon-only buttons */}
                  <div className="prev-toolbar">
                    <div className="prev-tb-right" style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
<button className="prev-icon-btn" onClick={() => toggleFullscreen(0)} title="Fullscreen">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"/>
                        </svg>
                      </button>
                      <button className="prev-icon-btn" onClick={goEdit} title="Edit">
                        <Icons.Edit sz={15} />
                      </button>
<button className="prev-icon-btn" onClick={() => downloadFile(genImg, token).then(() => toast.success('Downloaded!')).catch(() => toast.error('Download failed'))} title="Download">
                        <Icons.DL sz={15} />
                      </button>
                      <button className="prev-icon-btn del" onClick={async () => {
                        const isConfirmed = await confirmAlert({
                          title: 'Clear Results?',
                          message: 'Are you sure you want to clear these results?'
                        });
                        if (isConfirmed) setGenImgs([]);
                      }} title="Remove">
                        <Icons.Trash sz={15} />
                      </button>
                    </div>
                  </div>

                  {genImgs.length === 1 ? (
                    <img src={genImgs[0]} alt="" />
                  ) : (
                    <div className="ggrid" style={{ marginBottom: 12 }}>
                      {genImgs.map((url, i) => (
                        <div key={i} className="gitem" onClick={() => toggleFullscreen(i)}>
                          <img src={url} alt="" />
                          <div className="gbadge" style={{ background: 'rgba(0,0,0,.6)' }}>{i + 1}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  
                </div>
            </div>
          )}

          {(localJobs.length > 0 || recentImages.length > 0) && (
            <>
              <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--t2)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icons.Grid sz={13} /> {localJobs.length > 0 ? 'Generating...' : 'Recent Images'}
              </div>
              <div className="ggrid">
                {/* Pending shimmer cards */}
                {localJobs.map(j => (
                  <div key={j.id} className="gitem-pending">
                    <div className="pend-icon"><Icons.ImgFrame sz={28} /></div>
                    <div className="pend-label">{j.prompt || 'Generating...'}</div>
                    <div className="pend-model">{j.model}</div>
                    <div className="pend-bar-wrap"><div className="pend-bar" /></div>
                    <div style={{ fontSize: 9, color: 'var(--t3)', marginTop: 10, textAlign: 'center' }}>Estimated time 4 to 10 min</div>
                  </div>
                ))}
              </div>
              
              <div className="grouped-collections">
                {groupItemsByDate(recentImages).map(group => (
                  <div key={group.label} className="date-group-wrap">
                    <div className="date-group-label" style={{ marginBottom: 12 }}>{group.label}</div>
                    <div className="ggrid">
                      {group.items.map(i => (
                        <div key={i.id} className="gitem" 
                          onPointerDown={(e) => {
                            if (e.pointerType === 'mouse' && e.button !== 0) return;
                            startLongPress(i.id);
                          }}
                          onPointerUp={cancelLongPress}
                          onPointerLeave={cancelLongPress}
                          onPointerCancel={cancelLongPress}
                          onContextMenu={e => e.preventDefault()}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (longPressFired.current) {
                              e.preventDefault();
                              setTimeout(() => { longPressFired.current = false; }, 50);
                              return;
                            }
                            if (isSelectionMode) {
                              const isRemoving = selectedIds.includes(i.id);
                              const next = isRemoving ? selectedIds.filter(id => id !== i.id) : [...selectedIds, i.id];
                              if (next.length === 0) setIsSelectionMode(false);
                              setSelectedIds(next);
                            } else {
                              setLbItem(i);
                            }
                          }}
                          style={{ 
                            border: selectedIds.includes(i.id) ? '2px solid #3b82f6' : '1px solid transparent',
                            transform: selectedIds.includes(i.id) ? 'scale(0.96)' : 'none',
                            transition: 'all 0.2s',
                            boxSizing: 'border-box'
                          }}
                        >
                          {isSelectionMode && (
                            <div style={{
                              position: 'absolute', top: 8, right: 8, width: 22, height: 22,
                              borderRadius: '50%', background: selectedIds.includes(i.id) ? '#3b82f6' : 'rgba(0,0,0,0.5)',
                              border: '2px solid #fff', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                              {selectedIds.includes(i.id) && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                            </div>
                          )}
                          <img src={i.imageUrl} alt="" style={{ pointerEvents: 'none' }} />
                          <div className="gbadge" style={{ background: 'rgba(139,92,246,.12)', color: '#8b5cf6' }}><Icons.ImgFrame sz={11} /></div>
                          <div className="gact" style={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 6, zIndex: 15, opacity: 0, transition: 'opacity 0.2s' }}>
                            <button onClick={(e) => { e.stopPropagation(); import('../utils/downloadMedia').then(m => m.downloadMedia(i.videoUrl || i.imageUrl, i.type, token)); }} style={{ background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 6, padding: '5px', cursor: 'pointer', color: '#fff', display: 'flex' }}><Icons.DL sz={14} /></button>
                            <button onClick={(e) => { e.stopPropagation(); if(typeof handleDeleteItem === 'function') handleDeleteItem(i.id || i.$id); }} style={{ background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 6, padding: '5px', cursor: 'pointer', color: '#ef4444', display: 'flex' }}><Icons.Trash sz={14} /></button>
                          </div>
                          <div className="gov">
                            <div className="gm">
                              <div style={{ fontWeight: 700, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{i.prompt}</div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>{i.modelName || i.model}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {isSelectionMode && selectedIds.length > 0 && (
        <div style={{
           position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
           background: 'var(--s1)', border: '1px solid var(--b1)', borderRadius: 12,
           padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 16,
           boxShadow: '0 10px 40px rgba(0,0,0,0.5)', zIndex: 90
        }}>
           <div style={{ fontSize: 13, fontWeight: 600 }}>{selectedIds.length} selected</div>
           
           <div style={{ display: 'flex', gap: 8 }}>
             <button 
               disabled={isDownloading || isDeleting}
               onClick={handleBatchDownload}
               style={{
                 background: isDownloading ? 'rgba(59,130,246,0.5)' : '#3b82f6', color: '#fff', border: 'none', padding: '6px 16px',
                 borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: isDownloading ? 'not-allowed' : 'pointer',
                 display: 'flex', alignItems: 'center', gap: 6
               }}
             >
               <Icons.DL sz={14} /> {isDownloading ? 'Downloading...' : 'Download'}
             </button>
             
             <button 
               disabled={isDeleting || isDownloading}
               onClick={handleBatchDelete}
               style={{
                 background: isDeleting ? 'rgba(239,68,68,0.5)' : '#ef4444', color: '#fff', border: 'none', padding: '6px 16px',
                 borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: isDeleting ? 'not-allowed' : 'pointer'
               }}
             >
               {isDeleting ? 'Deleting...' : 'Delete Selected'}
             </button>
           </div>
        </div>
      )}

      {showClearConfirm && (
        <div className="mp-overlay" onClick={() => setShowClearConfirm(false)} style={{ zIndex: 80 }}>
          <div className="logout-confirm" onClick={e => e.stopPropagation()}>
            <div className="logout-icon" style={{ fontSize: 32, marginBottom: 8, textAlign: 'center' }}>🗑️</div>
            <div className="logout-title">Clear Prompt?</div>
            <div className="logout-desc">Are you sure you want to clear your current prompt and settings?</div>
            <div className="logout-btns">
              <button className="logout-btn cancel" onClick={() => setShowClearConfirm(false)}>
                Cancel
              </button>
              <button className="logout-btn confirm" onClick={() => {
                setPrompt('');
                setNegPrompt('');
                setRefFiles([]);
                setShowClearConfirm(false);
              }}>
                <Icons.Trash sz={13} /> Clear
              </button>
            </div>
          </div>
        </div>
      )}

      <Lightbox item={lbItem} onClose={() => setLbItem(null)} onDelete={handleDeleteItem} />

      <TemplateSlideWindow
        isOpen={showTemplates}
        onClose={() => setShowTemplates(false)}
        modelId={model}
        onSelect={async (tmpl) => {
          setPrompt(tmpl.prompt || '');
          if (tmpl.model) {
            let targetId = tmpl.model;
            const foundByLabel = IMG_MODELS.find(m => m.label === tmpl.model);
            if (foundByLabel) targetId = foundByLabel.id;
            setModel(targetId);
          }
          
          setShowTemplates(false);

          const mediaToLoad = tmpl.refImageUrl || tmpl.startImageUrl || tmpl.mediaUrl;
          if (mediaToLoad) {
            toast.loading('Loading template...', { id: 'tmpl-load' });
            const urls = Array.isArray(mediaToLoad) ? mediaToLoad : [mediaToLoad];
            for (const url of urls) {
              if (url) await processReferenceImage(url, tmpl.prompt, 'use-as-reference');
            }
            toast.success('Template applied!', { id: 'tmpl-load' });
          } else {
            toast.success('Template applied!');
          }
        }}
      />

      <MediaLibraryModal
        open={libOpen}
        onClose={() => setLibOpen(false)}
        allowedTypes={['image']}
        maxSelect={maxRefs - refFiles.length}
        maxTypeCounts={{ image: maxRefs - refFiles.length, video: 0, audio: 0 }}
        onSelect={(assets) => {
          setRefFiles(prev => [...prev, ...assets].slice(0, maxRefs));
        }}
      />
    </>
  );
}

